import { getActiveOrganisationId } from '../organisationContext';

export function isMissingEndColumnsError(error) {
  const message = String(error?.message || '').toLowerCase();

  return (
    message.includes('schema cache') ||
    message.includes('column') ||
    message.includes('end_date') ||
    message.includes('end_time')
  );
}

export function isMissingTrainingBookingColumnsError(error) {
  const code = String(error?.code || '').toUpperCase();
  const message = String(error?.message || '').toLowerCase();

  const trainingColumns = [
    'organisation_id',
    'booking_type',
    'student_user_id',
    'instructor_user_id',
    'course_enrolment_id',
    'created_by_user_id',
  ];

  return (
    (code === '42703' || code === 'PGRST204' || message.includes('schema cache')) &&
    trainingColumns.some((column) => message.includes(column))
  );
}

export function isMissingTechLogStatusColumnError(error) {
  const code = String(error?.code || '').toUpperCase();
  const message = String(error?.message || '').toLowerCase();

  return (
    (code === '42703' || code === 'PGRST204' || message.includes('schema cache')) &&
    message.includes('tech_log_status')
  );
}

function isBookingsTimeOrderError(error) {
  const code = String(error?.code || '').toUpperCase();
  const message = String(error?.message || '').toLowerCase();

  return (
    code === 'PGRST100' ||
    message.includes('failed to parse order') ||
    (message.includes('order') && message.includes('time'))
  );
}

export async function getCurrentOrganisationId(supabase) {
  const {
    data: { user },
    error: authError,
  } = await supabase.auth.getUser();

  if (authError || !user?.id) {
    return '';
  }

  const { data, error } = await supabase
    .from('users')
    .select('organisation_id')
    .eq('auth_id', user.id)
    .maybeSingle();

  if (error) {
    return '';
  }

  return String(data?.organisation_id || '').trim();
}

export async function getReadOrganisationId(supabase) {
  const activeOrganisationId = getActiveOrganisationId();
  if (!activeOrganisationId) {
    return getCurrentOrganisationId(supabase);
  }

  const {
    data: { user },
    error: authError,
  } = await supabase.auth.getUser();

  if (authError || !user?.id) {
    return '';
  }

  // Client selection narrows queries only; PostgreSQL RLS authorizes the scope.
  return activeOrganisationId;
}

export async function runBookingsQueryWithTimeOrderFallback(buildQuery) {
  let result = await buildQuery(true);

  if (!result.error || !isBookingsTimeOrderError(result.error)) {
    return result;
  }

  result = await buildQuery(false);
  return result;
}

export async function fetchAircraftList(supabase) {
  const organisationId = await getReadOrganisationId(supabase);

  if (!organisationId) {
    return {
      data: [],
      error: new Error('Could not identify the current organisation.'),
    };
  }

  return supabase
    .from('aircraft')
    .select('id, registration, icao_type, status')
    .eq('organisation_id', organisationId)
    .order('registration', { ascending: true });
}

export async function fetchUsersList(supabase) {
  const organisationId = await getCurrentOrganisationId(supabase);

  if (!organisationId) {
    return { data: [], error: new Error('Could not identify the current organisation.') };
  }

  return supabase
    .from('users')
    .select('id, full_name, email, phone')
    .eq('organisation_id', organisationId);
}

export async function fetchBookingRows(supabase) {
  const organisationId = await getReadOrganisationId(supabase);

  if (!organisationId) {
    return { data: [], error: new Error('Could not identify the current organisation.') };
  }

  const fields = {
    training: 'organisation_id, booking_type, student_user_id, instructor_user_id, course_enrolment_id, created_by_user_id, ',
    end: 'end_date, end_time, ',
    closeout: 'tech_log_status, ',
  };
  const runQuery = (includeTraining, includeEnd, includeCloseout) =>
    runBookingsQueryWithTimeOrderFallback((includeTimeOrder) => {
      let query = supabase
        .from('bookings')
        .select(`${includeTraining ? fields.training : ''}id, user_id, aircraft_id, date, time, ${includeEnd ? fields.end : ''}duration, notes, briefing_status, ${includeCloseout ? fields.closeout : ''}briefing_data, aircraft!bookings_organisation_aircraft_fkey!inner(organisation_id)`)
        .eq('aircraft.organisation_id', organisationId)
        .order('date', { ascending: true });

      if (includeTimeOrder) query = query.order('time', { ascending: true });
      return query;
    });

  let includeTraining = true;
  let includeEnd = true;
  let includeCloseout = true;
  let result = await runQuery(includeTraining, includeEnd, includeCloseout);
  if (result.error && isMissingTechLogStatusColumnError(result.error)) {
    includeCloseout = false;
    result = await runQuery(includeTraining, includeEnd, includeCloseout);
  }
  if (result.error && isMissingTrainingBookingColumnsError(result.error)) {
    includeTraining = false;
    result = await runQuery(includeTraining, includeEnd, includeCloseout);
  }
  if (result.error && isMissingEndColumnsError(result.error)) {
    includeTraining = false;
    includeEnd = false;
    result = await runQuery(includeTraining, includeEnd, includeCloseout);
    if (!result.error) {
      result.data = (result.data || []).map((row) => ({
        ...row,
        end_date: null,
        end_time: null,
      }));
    }
  }

  return result;
}

export async function fetchTrainingBookingOptions(supabase) {
  const organisationId = await getCurrentOrganisationId(supabase);
  const empty = { studentProfiles: [], instructorProfiles: [], enrolments: [] };

  if (!organisationId) {
    return { ...empty, error: new Error('Could not identify the current organisation.') };
  }

  const [students, instructors, enrolments] = await Promise.all([
    supabase
      .from('student_profiles')
      .select('id, user_id, status')
      .eq('organisation_id', organisationId)
      .eq('status', 'active'),
    supabase
      .from('instructor_profiles')
      .select('id, user_id, status')
      .eq('organisation_id', organisationId)
      .eq('status', 'active'),
    supabase
      .from('course_enrolments')
      .select('id, student_profile_id, course_id, primary_instructor_profile_id, status, training_courses!inner(code, name)')
      .eq('organisation_id', organisationId)
      .eq('status', 'active'),
  ]);
  const error = students.error || instructors.error || enrolments.error || null;

  return {
    studentProfiles: students.data || [],
    instructorProfiles: instructors.data || [],
    enrolments: enrolments.data || [],
    error,
  };
}

export async function fetchAircraftStatus(supabase, aircraftId) {
  const organisationId = await getReadOrganisationId(supabase);

  if (!organisationId) {
    return {
      data: null,
      error: new Error('Could not identify the current organisation.'),
    };
  }

  return supabase
    .from('aircraft')
    .select('registration, status')
    .eq('id', aircraftId)
    .eq('organisation_id', organisationId)
    .maybeSingle();
}